import { useStore } from '@nanostores/react'
import iconArrow from 'assets/image/icons/png/arrow-green.png'
import clsx from 'clsx'
import { FullLoader } from 'components/atoms/FullLoader/FullLoader'
import { CONSTS } from 'config/objectConst'
import { type Campaign, campaignApi } from 'features/campaigns'
import { executionsConst, executionsUtils } from 'features/executions'
import { type CSSProperties, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { initHoverActions } from 'shared/position/hover-actions'
import { Param } from 'shared/search-adapters'
import { contextActions } from 'shared/styles'

import type { Execution } from '../../types'

import { api } from '../../api'
import css from './PopoverInfo.module.scss'

const { $id, popoverListeners, triggerListeners, useSyncPopoverState } =
	initHoverActions({ anchorCN: css.currentAnchor })

PopoverInfo.triggerListeners = triggerListeners

export function PopoverInfo() {
	const ref = useSyncPopoverState()

	return (
		<div
			className={clsx(css.menu, contextActions.menu, 'app-scroll-thin')}
			{...popoverListeners}
			ref={ref}
		>
			<Content />
		</div>
	)
}

function CampaignItem({
	archived,
	execution,
	id,
	name,
	steps
}: Campaign.Campaign & { execution?: Execution; steps?: Campaign.Campaign['steps'] }) {
	const full_name = execution?.linkedin_contact.full_name

	return (
		<div className={css.item} key={name}>
			<Link
				className={css.link}
				target='_blank'
				to={archived ? `/archive/${id}` : `/campaigns/${id}/contacts?${Param.SearchQuery}=${full_name}`}
			>
				{name}
				<img alt='infoBlackIcon' className='icon-arrow' src={iconArrow} />
			</Link>
			{execution && (
				<div
					className={css.state}
					style={
						{
							'--active': executionsConst.StepColor[execution.human_state]
						} as CSSProperties
					}
				>
					{executionsUtils.getExecutionTitle(execution.state, steps)}
				</div>
			)}
		</div>
	)
}

function Content() {
	const id = useStore($id)

	const executionsQuery = api.useExecutionsQuery(
		{
			$filter: `(linkedin_contact/id eq '${id}')`,
			$orderby: 'created desc',
			$take: 10
		},
		{
			skip: !id
		}
	)

	const { $filter, map } = useMemo(() => {
		const items = executionsQuery.data?.documents ?? []

		const ids: string[] = []
		const map: Record<string, Execution> = {}

		for (const execution of items) {
			const id = execution.campaign_id
			ids.push(id)
			map[id] = execution
		}

		const $filter = ids.length ? `id in ('${ids.join("', '")}')` : ''

		return {
			$filter,
			map
		}
	}, [executionsQuery.data?.documents])

	const campaignsQuery = campaignApi.useCampaignsPageQuery(
		{
			$filter,
			$orderby: 'created desc',
			$take: CONSTS.PAGE_SIZE
		},
		{
			skip: !$filter
		}
	)

	const showLoader = executionsQuery.isFetching || campaignsQuery.isFetching

	if (showLoader) {
		return <FullLoader size={20} />
	}

	const campaigns = campaignsQuery.data?.documents

	return (
		<>
			<p className={css.title}>Participate in campaigns</p>

		{campaigns?.length ? (
			campaigns!.map(item => (
				<CampaignItem {...item} execution={map[item.id]} key={item.id} steps={item.steps} />
			))
		) : (
			<div className={css.subTitle}>No campaigns</div>
		)}
		</>
	)
}
